import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener, Injector,
  Input,
  Output,
  SimpleChanges,
  ViewChild
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { CommonModule } from "@angular/common";
import { SvgIconComponent } from "../svg-icon/svg-icon.component";
import { animate, state, style, transition, trigger } from "@angular/animations";
import { ClickOutsideDirective } from "../../directives/click-outside.directive";
import {
  CdkConnectedOverlay,
  CdkOverlayOrigin,
  ConnectedPosition,
  Overlay,
  ScrollStrategy
} from "@angular/cdk/overlay";
import { CommonUtils } from "../../utils/common-utils";

@Component({
  selector: "app-multi-select",
  standalone: true,
  templateUrl: "./multi-select.component.html",
  styleUrls: ["./multi-select.component.scss"],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    SvgIconComponent,
    ClickOutsideDirective,
    CdkConnectedOverlay,
    CdkOverlayOrigin
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiSelectComponent),
      multi: true
    }
  ],
  animations: [
    trigger("dropdownAnimation", [
      state("open", style({
        height: "*",
        opacity: 1,
        visibility: "visible",
        overflow: "auto"
      })),
      state("closed", style({
        height: "0px",
        opacity: 0,
        visibility: "hidden",
        overflow: "auto"
      })),
      transition("open <=> closed", animate("100ms ease-in-out"))
    ])
  ]
})
export class MultiSelectComponent implements ControlValueAccessor {
  @Input() options: any[] = [];
  @Input() valueKey!: string;
  @Input() displayKey!: string;
  @Input() isMultiSelect: boolean = true;
  @Input() placeholder!: string;
  @Input() selectedItems: any[] = [];
  @Input() showChips: boolean = true;
  @Input() disable: boolean = false;
  @Input() readOnly: boolean = false;
  @Input() returnRawValue: boolean = false;

  @Output() selectionChange = new EventEmitter<any>();
  @Output() selectedItemsChange = new EventEmitter<any[]>();

  @ViewChild("trigger") trigger!: CdkOverlayOrigin;
  @ViewChild("dropdown", { static: false }) dropdown!: ElementRef;

  @HostListener('window:resize')
  onResize() {
    this.updateWidth();
  }

  filteredOptions: any[] = [];
  searchQuery: string = "";
  dropdownOpen = false;
  triggerWidth: number = 0;
  highlightedIndex = -1;
  public ngControl: NgControl | null = null;

  onChange: (value: any) => void = () => {
  };
  onTouched: () => void = () => {
  };

  constructor(private cdr: ChangeDetectorRef,
              private injector: Injector,
              private overlay: Overlay) {
  }

  ngOnInit() {
    this.ngControl = this.injector.get(NgControl, null);
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
    this.filteredOptions = this.options;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["options"]) {
      this.filteredOptions = this.options;

      if (!this.selectedItems || this.selectedItems.length === 0) {
        return;
      }

      const validSelectedItems = this.selectedItems.filter(selectedItem =>
        this.options.some(option =>
          option[this.valueKey] === selectedItem[this.valueKey]
        )
      );
      if (validSelectedItems.length !== this.selectedItems.length) {
        this.selectedItems = validSelectedItems;
        const raw = this.returnRawValue ? this.selectedItems : this.selectedItems.map(i => i[this.valueKey]);
        this.onChange(this.isMultiSelect ? raw : raw[0]);
        this.selectedItemsChange.emit(this.selectedItems);
      }
    }
  }

  ngAfterViewInit() {
    this.updateWidth();
    this.cdr.detectChanges();
  }

  private updateWidth() {
    if (this.trigger?.elementRef?.nativeElement) {
      this.triggerWidth = this.trigger.elementRef.nativeElement.offsetWidth || 0;
    }
  }

  writeValue(value: any): void {
    setTimeout(() => {
      if (Array.isArray(value)) {
        if (value.length && typeof value[0] === 'string') {
          this.selectedItems = value
            .map(val => this.options.find(option => option[this.valueKey] === val))
            .filter(Boolean);
        } else {
          this.selectedItems = value;
        }
      } else if (CommonUtils.isNotNil(value)) {
        const selected =
          ['string', 'number', 'boolean'].includes(typeof value)
            ? this.options.find(option => option[this.valueKey] === value)
            : value;

        this.selectedItems = selected ? [selected] : [];
      } else {
        this.selectedItems = [];
      }

      if (this.selectedItems.length) {
        const raw = this.returnRawValue ? this.selectedItems : this.selectedItems.map(i => i[this.valueKey]);
        this.onChange(this.isMultiSelect ? raw : raw[0]);
      }
    });
  }

  toggleSelection(option: any): void {
    if (this.isMultiSelect) {
      if (this.isSelected(option)) {
        this.selectedItems = this.selectedItems.filter((item) => item[this.valueKey] !== option[this.valueKey]);
      } else {
        this.selectedItems.push(option);
      }
      this.onChange(this.returnRawValue ? this.selectedItems : this.selectedItems.map(item => item[this.valueKey]));
    } else {
      const previousSelection = this.selectedItems[0];
      if (!previousSelection || previousSelection[this.valueKey] !== option[this.valueKey]) {
        this.selectedItems.length = 0;
        this.selectedItems.push(option);
        this.dropdownOpen = false;
        this.resetSearchQuery();
        this.selectionChange.emit(option);
        this.onChange(this.returnRawValue ? option : option[this.valueKey]);
      }
    }
    this.selectedItemsChange.emit(this.selectedItems);
    this.onTouched();
  }

  resetSearchQuery(): void {
    this.searchQuery = "";
    this.filteredOptions = this.options;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disable = isDisabled;
  }


  setOptions(options: any[]): void {
    this.options = options;
    this.filteredOptions = [...this.options];
  }

  filterOptions(): void {
    if (!this.searchQuery) {
      this.filteredOptions = this.options;
    } else {
      this.filteredOptions = this.options.filter(option =>
        option[this.displayKey].toLowerCase().includes(this.searchQuery.toLowerCase())
      );
    }
  }

  isSelected(option: any): boolean {
    return this.selectedItems.some((item: any) => item[this.valueKey] === option[this.valueKey]);
  }

  toggleDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.dropdownOpen = !this.dropdownOpen;
  }

  removeChip(option: any): void {
    this.selectedItems = this.selectedItems.filter((item) => item[this.valueKey] !== option[this.valueKey]);
    this.onChange(this.returnRawValue ? this.selectedItems : this.selectedItems.map(item => item[this.valueKey]));
    this.selectedItemsChange.emit(this.selectedItems);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  handleInputKeydown(event: KeyboardEvent): void {
    if (event.key === "Enter") {
      this.dropdownOpen = !this.dropdownOpen;
    }
  }

  // Handle keydown on individual options
  handleOptionKeydown(event: KeyboardEvent, option: any): void {
    console.log("event.key => ", event.key, option);
    if (event.key === "Enter") {
      this.toggleSelection(option);
    } else if (event.key === "ArrowDown") {
      this.highlightNextOption();
    } else if (event.key === "ArrowUp") {
      this.highlightPreviousOption();
    } else if (event.key === " ") {
      this.toggleSelection(option);
    }
  }

  highlightNextOption(): void {
    if (this.highlightedIndex < this.options.length - 1) {
      this.highlightedIndex++;
    }
  }

  highlightPreviousOption(): void {
    if (this.highlightedIndex > 0) {
      this.highlightedIndex--;
    }
  }

  highlightOption(index: number): void {
    this.highlightedIndex = index;
  }

  @HostListener("document:click", ["$event"])
  onClickOutside(event: MouseEvent): void {
    if (this.dropdown && this.dropdown.nativeElement && this.dropdownOpen) {
      if (!this.dropdown.nativeElement.contains(event.target)) {
        this.dropdownOpen = false;
        this.resetSearchQuery();
      }
    }
  }

  hasError() {
    return this.ngControl?.control?.invalid && this.ngControl.control?.touched && this.ngControl.control?.errors;
  }
}
